import { useEffect } from 'react';
import { CheckCircle2, Info, AlertTriangle, XCircle, X } from 'lucide-react';
import { useToastStore, type ToastKind } from '../state/toast-store.js';

/**
 * Renders the toast stack at the bottom-right of the viewport.
 *
 * Behaviour:
 *  - Stack is rendered with `role="status"` + `aria-live="polite"` so screen
 *    readers announce new toasts without interrupting the player mid-move.
 *  - Click anywhere on a toast to dismiss it.
 *  - `Esc` dismisses the most-recent toast (top of the stack).
 *  - Each toast auto-dismisses after `durationMs`; `durationMs === 0` is sticky.
 *
 * The host itself is `aria-hidden` when the stack is empty so it doesn't
 * produce a phantom announcement on every page navigation.
 */
export function ToastHost() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);
  const dismissTop = useToastStore((s) => s.dismissTop);

  useEffect(() => {
    if (toasts.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        dismissTop();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toasts.length, dismissTop]);

  if (toasts.length === 0) return null;

  return (
    <output
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-full max-w-sm flex-col gap-2"
      data-testid="toast-host"
      data-appdom-id="toast-host"
      data-appdom-type="ToastHost"
      aria-live="polite"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </output>
  );
}

function ToastCard({ toast, onDismiss }: { readonly toast: import('../state/toast-store.js').Toast; readonly onDismiss: () => void }) {
  // Auto-dismiss timer; resets on unmount (no leak if a toast disappears
  // from under us — the cleanup callback fires).
  useEffect(() => {
    if (toast.durationMs <= 0) return;
    const timer = window.setTimeout(onDismiss, toast.durationMs);
    return () => window.clearTimeout(timer);
  }, [toast.durationMs, onDismiss]);

  const palette = KIND_PALETTE[toast.kind];

  return (
    <button
      type="button"
      onClick={onDismiss}
      data-testid="toast"
      data-appdom-id="toast"
      data-appdom-type="Toast"
      data-toast-kind={toast.kind}
      // Info toasts ("Move played", "Robot replied") are transient and sit over
      // the board's bottom-right squares; they must not swallow a tap meant for
      // the square underneath (sticky under AUDIT_CAPTURE, they never left).
      // Other kinds stay clickable to dismiss.
      className={`${toast.kind === 'info' ? 'pointer-events-none' : 'pointer-events-auto'} flex items-start gap-3 rounded-2xl border-2 ${palette.border} ${palette.bg} p-3 text-left shadow-lg transition-transform hover:scale-[1.02]`}
    >
      <span className={`mt-0.5 shrink-0 ${palette.icon}`}>
        <KIND_ICON kind={toast.kind} />
      </span>
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-extrabold ${palette.title}`}>{toast.title}</p>
        {toast.body && <p className={`mt-0.5 text-xs font-semibold ${palette.body}`}>{toast.body}</p>}
      </div>
      <span className={`shrink-0 ${palette.icon}`} aria-hidden>
        <X size={14} />
      </span>
    </button>
  );
}

const KIND_ICON = ({ kind }: { kind: ToastKind }) => {
  switch (kind) {
    case 'success':
      return <CheckCircle2 size={18} />;
    case 'warning':
      return <AlertTriangle size={18} />;
    case 'error':
      return <XCircle size={18} />;
    default:
      return <Info size={18} />;
  }
};

interface Palette {
  readonly border: string;
  readonly bg: string;
  readonly icon: string;
  readonly title: string;
  readonly body: string;
}

const KIND_PALETTE: Record<ToastKind, Palette> = {
  info: {
    border: 'border-sky/40',
    bg: 'bg-cream',
    icon: 'text-sky',
    title: 'text-ink',
    body: 'text-ink/70',
  },
  success: {
    border: 'border-leaf/50',
    bg: 'bg-cream',
    icon: 'text-leaf',
    title: 'text-ink',
    body: 'text-ink/70',
  },
  warning: {
    border: 'border-sun/60',
    bg: 'bg-cream',
    icon: 'text-sun',
    title: 'text-ink',
    body: 'text-ink/70',
  },
  error: {
    border: 'border-berry/60',
    bg: 'bg-cream',
    icon: 'text-berry',
    title: 'text-berry',
    body: 'text-ink/70',
  },
};
